import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getFileUrl } from '../utils/fileUtils';
import API from '../utils/api';
import { FaIndustry, FaMapMarkerAlt, FaGlobe, FaUsers, FaCalendar, FaEdit, FaSave, FaTimes, FaCheckCircle, FaClock, FaLinkedin, FaTwitter, FaFacebook, FaInstagram, FaSync } from 'react-icons/fa';

const CompanyProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [company, setCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    industry: '',
    location: {
      city: '',
      state: '',
      country: 'India'
    },
    website: '',
    companySize: '',
    founded: '',
    logo: null,
    specialties: '',
    benefits: '',
    socialLinks: {
      linkedin: '',
      twitter: '',
      facebook: '',
      instagram: ''
    }
  });
  const [documents, setDocuments] = useState({
    aadharCard: null,
    panCard: null,
    gstCertificate: null,
    udyamAadhar: null
  });
  const [uploadingDoc, setUploadingDoc] = useState(null);

  const industries = [
    'IT & Software',
    'Finance',
    'Healthcare',
    'Manufacturing',
    'Retail',
    'Education',
    'Energy',
    'Telecommunications',
    'Media & Entertainment',
    'Real Estate',
    'Transportation',
    'Hospitality',
    'Other'
  ];

  const companySizes = ['1-10', '11-50', '51-200', '201-500', '501-1000', '1000+'];

  useEffect(() => {
    fetchCompanyProfile();

    // Auto-refresh verification status every 10 seconds
    const pollInterval = setInterval(() => {
      if (!editMode && !saving) {
        fetchCompanyProfile();
      }
    }, 10000);

    return () => clearInterval(pollInterval);
  }, [editMode, saving]);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      const response = await API.get('/companies/my-company');
      console.log('✅ Fetched company profile:', response.data);
      console.log('Logo from DB:', response.data.logo);
      console.log('Verification Status:', response.data.documentVerification?.status);
      setCompany(response.data);
      populateForm(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.info('You need to create a company profile first');
        navigate('/create-company');
      } else {
        toast.error('Failed to load company profile');
        console.error(error);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCompanyProfile();
    toast.success('Profile refreshed!');
  };

  const populateForm = (companyData) => {
    setFormData({
      name: companyData.name || '',
      description: companyData.description || '',
      industry: companyData.industry || '',
      location: {
        city: companyData.location?.city || '',
        state: companyData.location?.state || '',
        country: companyData.location?.country || 'India'
      },
      website: companyData.website || '',
      companySize: companyData.companySize || '',
      founded: companyData.founded || '',
      logo: null,
      specialties: companyData.specialties?.join(', ') || '',
      benefits: companyData.benefits?.join(', ') || '',
      socialLinks: {
        linkedin: companyData.socialLinks?.linkedin || '',
        twitter: companyData.socialLinks?.twitter || '',
        facebook: companyData.socialLinks?.facebook || '',
        instagram: companyData.socialLinks?.instagram || ''
      }
    });

    // Populate documents
    if (companyData.documents) {
      setDocuments({
        aadharCard: companyData.documents.aadharCard || null,
        panCard: companyData.documents.panCard || null,
        gstCertificate: companyData.documents.gstCertificate || null,
        udyamAadhar: companyData.documents.udyamAadhar || null
      });
    }
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleDocumentChange = async (e, docType) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDoc(docType);
    const docFormData = new FormData();
    docFormData.append('document', file);
    docFormData.append('documentType', docType);

    try {
      const response = await API.post(`/companies/${company._id}/documents`, docFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('Document uploaded:', response.data);

      // Update local documents state
      setDocuments(prev => ({
        ...prev,
        [docType]: response.data.filePath
      }));

      // Fetch fresh company data to sync with database
      const freshCompany = await API.get('/companies/my-company');
      setCompany(freshCompany.data);

      toast.success(`${docType} uploaded successfully`);
    } catch (error) {
      console.error('Document upload failed:', error);
      toast.error(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Company description is required');
      return;
    }
    if (!formData.industry) {
      toast.error('Industry is required');
      return;
    }
    if (!formData.location.city.trim()) {
      toast.error('City is required');
      return;
    }

    setSaving(true);

    try {
      const specialtiesArray = formData.specialties
        ? formData.specialties.split(',').map(s => s.trim()).filter(s => s)
        : [];

      const benefitsArray = formData.benefits
        ? formData.benefits.split(',').map(s => s.trim()).filter(s => s)
        : [];

      const updateData = {
        name: formData.name,
        description: formData.description,
        industry: formData.industry,
        location: formData.location,
        website: formData.website,
        specialties: specialtiesArray,
        benefits: benefitsArray,
        socialLinks: formData.socialLinks
      };

      // Only include optional fields if they have values
      if (formData.companySize && formData.companySize.trim()) {
        updateData.companySize = formData.companySize;
      }
      if (formData.founded) {
        updateData.founded = parseInt(formData.founded);
      }

      // Upload logo if new file selected
      if (formData.logo && formData.logo instanceof File) {
        const logoFormData = new FormData();
        logoFormData.append('logo', formData.logo);

        try {
          const logoRes = await API.post(`/companies/${company._id}/logo`, logoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          console.log('Logo uploaded:', logoRes.data);
          toast.success('Logo uploaded successfully');
        } catch (logoError) {
          console.error('Logo upload failed:', logoError);
          toast.warning('Company updated but logo upload failed');
        }
      }

      // Update company details
      const updatedCompany = await API.put(`/companies/${company._id}`, updateData);
      console.log('Company updated via PUT:', updatedCompany.data);

      // Fetch fresh company data to ensure we have the latest logo
      const freshCompany = await API.get('/companies/my-company');
      console.log('✅ Fresh company data after save:', freshCompany.data);
      console.log('Logo in fresh data:', freshCompany.data.logo);
      setCompany(freshCompany.data);
      populateForm(freshCompany.data);
      setEditMode(false);
      toast.success('Company profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update company profile');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    populateForm(company);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-3xl font-bold mb-4">Company Profile</h1>
        <p className="text-gray-600 mb-6">No company profile found</p>
        <button
          onClick={() => navigate('/create-company')}
          className="btn-primary"
        >
          Create Company Profile
        </button>
      </div>
    );
  }

  const getVerificationStatus = () => {
    const status = company.documentVerification?.status;
    if (status === 'verified') {
      return <span className="flex items-center gap-2 text-green-600"><FaCheckCircle /> Verified</span>;
    } else if (status === 'pending') {
      return <span className="flex items-center gap-2 text-yellow-600"><FaClock /> Pending Verification</span>;
    } else if (status === 'rejected') {
      return <span className="flex items-center gap-2 text-red-600"><FaTimes /> Rejected - {company.documentVerification?.rejectionReason}</span>;
    }
    return <span className="flex items-center gap-2 text-gray-600"><FaClock /> Not Submitted</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pb-16">
      {/* ── TOP GRADIENT BANNER ── */}
      <div className="bg-gradient-to-r from-[#00c2ab] to-[#009688] pt-12 pb-32 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-10">
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="w-full h-full">
            <polygon fill="white" points="0,100 100,0 100,100" />
          </svg>
        </div>
        <div className="container mx-auto max-w-5xl relative z-10 flex justify-between items-end">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-2 drop-shadow-md">
              Company Profile
            </h1>
            <p className="text-teal-50 text-lg">
              {editMode ? 'Update your company details and branding.' : 'Manage your company identity and documentation.'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm border border-white/30 px-5 py-2.5 rounded-xl transition-all disabled:opacity-50 font-medium"
              title="Refresh verification status"
            >
              <FaSync className={refreshing ? 'animate-spin' : ''} /> Refresh
            </button>
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-2 bg-white text-[#00c2ab] hover:bg-gray-50 px-5 py-2.5 rounded-xl transition-all shadow-md font-bold"
              >
                <FaEdit /> Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 -mt-20 relative z-20 max-w-5xl">
        {!editMode ? (
          /* View Mode */
          <div className="space-y-6">
            
            {/* Main Company Card */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10">
              {/* Verification Status inside card */}
              <div className="flex justify-between items-center mb-8 pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 font-medium text-sm uppercase tracking-wide">Verification Status</span>
                  <div className="bg-gray-50 px-3 py-1 rounded-full border border-gray-100 font-medium text-sm shadow-sm">
                    {getVerificationStatus()}
                  </div>
                </div>
                {company.documentVerification?.status === 'rejected' && (
                  <p className="text-sm text-red-600 font-medium">Note: {company.documentVerification?.adminNotes}</p>
                )}
              </div>

              <div className="flex flex-col md:flex-row gap-8 items-start">
                {company.logo ? (
                  <img
                    src={getFileUrl(company.logo.startsWith('uploads/') ? company.logo : `uploads/${company.logo}`)}
                    alt={company.name}
                    className="w-40 h-40 object-cover rounded-2xl shadow-lg border-4 border-white"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=00c2ab&color=fff&size=256&bold=true`;
                    }}
                  />
                ) : (
                  <div className="w-40 h-40 bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl flex items-center justify-center text-teal-300 shadow-inner border border-teal-50">
                    <FaIndustry size={48} />
                  </div>
                )}
                <div className="flex-1">
                  <h2 className="text-4xl font-extrabold text-gray-900 mb-3">{company.name}</h2>
                  <p className="text-gray-600 text-lg mb-6 leading-relaxed">{company.description}</p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-[#00c2ab]"><FaIndustry /></div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase">Industry</p>
                        <p className="font-medium">{company.industry}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-[#00c2ab]"><FaUsers /></div>
                      <div>
                        <p className="text-xs text-gray-400 font-semibold uppercase">Company Size</p>
                        <p className="font-medium">{company.companySize || 'N/A'} employees</p>
                      </div>
                    </div>
                    {company.founded && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-[#00c2ab]"><FaCalendar /></div>
                        <div>
                          <p className="text-xs text-gray-400 font-semibold uppercase">Founded</p>
                          <p className="font-medium">{company.founded}</p>
                        </div>
                      </div>
                    )}
                    {company.website && (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-[#00c2ab]"><FaGlobe /></div>
                        <div>
                          <p className="text-xs text-gray-400 font-semibold uppercase">Website</p>
                          <a href={company.website} target="_blank" rel="noopener noreferrer" className="font-medium text-[#00c2ab] hover:underline">
                            {company.website.replace(/^https?:\/\//, '')}
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Grid for supporting cards */}
            <div className="grid md:grid-cols-2 gap-6">
              
              {/* Contact Information */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Contact Information</h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-semibold text-gray-400 uppercase block mb-1">Registered Email</span>
                    <p className="font-medium text-gray-800">{company.contactInfo?.registeredEmail || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-gray-400 uppercase block mb-1">Phone Number</span>
                    <p className="font-medium text-gray-800">{company.contactInfo?.registeredPhone || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-[#00c2ab]" /> Location
                </h3>
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-semibold text-gray-400 uppercase block mb-1">Headquarters</span>
                    <p className="font-medium text-gray-800 text-lg">
                      {company.location?.city}, {company.location?.state}
                    </p>
                    <p className="text-gray-500 mt-1">{company.location?.country}</p>
                  </div>
                </div>
              </div>

            {/* Specialties & Benefits */}
            {(company.specialties?.length > 0 || company.benefits?.length > 0) && (
              <div className="grid md:grid-cols-2 gap-6">
                {company.specialties?.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Specialties</h3>
                    <div className="flex flex-wrap gap-2">
                      {company.specialties.map((spec, idx) => (
                        <span key={idx} className="bg-teal-50 text-[#00c2ab] border border-teal-100 px-4 py-1.5 rounded-full text-sm font-medium shadow-sm">
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {company.benefits?.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow">
                    <h3 className="text-xl font-bold text-gray-900 mb-6">Benefits</h3>
                    <div className="flex flex-wrap gap-2">
                      {company.benefits.map((benefit, idx) => (
                        <span key={idx} className="bg-blue-50 text-blue-600 border border-blue-100 px-4 py-1.5 rounded-full text-sm font-medium shadow-sm">
                          {benefit}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Social Links */}
            {Object.values(company.socialLinks || {}).some(v => v) && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Social Links</h3>
                <div className="flex gap-5">
                  {company.socialLinks?.linkedin && (
                    <a href={company.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-700 transition-colors transform hover:scale-110">
                      <FaLinkedin size={32} />
                    </a>
                  )}
                  {company.socialLinks?.twitter && (
                    <a href={company.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors transform hover:scale-110">
                      <FaTwitter size={32} />
                    </a>
                  )}
                  {company.socialLinks?.facebook && (
                    <a href={company.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-600 transition-colors transform hover:scale-110">
                      <FaFacebook size={32} />
                    </a>
                  )}
                  {company.socialLinks?.instagram && (
                    <a href={company.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-600 transition-colors transform hover:scale-110">
                      <FaInstagram size={32} />
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Company Documents */}
            {company.documents && Object.values(company.documents).some(v => v) && (
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center text-[#00c2ab]">📄</span>
                  Registration Documents
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {company.documents.aadharCard && (
                    <div className="flex items-center justify-between p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-[#00c2ab] transition-colors shadow-sm">
                      <span className="font-semibold text-gray-800">Aadhar Card</span>
                      <a
                        href={company.documents.aadharCard.startsWith('http') 
                          ? company.documents.aadharCard
                          : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}/${company.documents.aadharCard}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white border-2 border-gray-200 text-gray-700 hover:text-[#00c2ab] hover:border-[#00c2ab] px-4 py-1.5 rounded-lg text-sm font-semibold transition-all shadow-sm"
                      >
                        View
                      </a>
                    </div>
                  )}
                  {company.documents.panCard && (
                    <div className="flex items-center justify-between p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-[#00c2ab] transition-colors shadow-sm">
                      <span className="font-semibold text-gray-800">PAN Card</span>
                      <a
                        href={company.documents.panCard.startsWith('http')
                          ? company.documents.panCard
                          : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}/${company.documents.panCard}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white border-2 border-gray-200 text-gray-700 hover:text-[#00c2ab] hover:border-[#00c2ab] px-4 py-1.5 rounded-lg text-sm font-semibold transition-all shadow-sm"
                      >
                        View
                      </a>
                    </div>
                  )}
                  {company.documents.gstCertificate && (
                    <div className="flex items-center justify-between p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-[#00c2ab] transition-colors shadow-sm">
                      <span className="font-semibold text-gray-800">GST Certificate</span>
                      <a
                        href={company.documents.gstCertificate.startsWith('http')
                          ? company.documents.gstCertificate
                          : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}/${company.documents.gstCertificate}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white border-2 border-gray-200 text-gray-700 hover:text-[#00c2ab] hover:border-[#00c2ab] px-4 py-1.5 rounded-lg text-sm font-semibold transition-all shadow-sm"
                      >
                        View
                      </a>
                    </div>
                  )}
                  {company.documents.udyamAadhar && (
                    <div className="flex items-center justify-between p-5 bg-gray-50 rounded-xl border border-gray-200 hover:border-[#00c2ab] transition-colors shadow-sm">
                      <span className="font-semibold text-gray-800">Udyam Aadhar</span>
                      <a
                        href={company.documents.udyamAadhar.startsWith('http')
                          ? company.documents.udyamAadhar
                          : `${(process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '')}/${company.documents.udyamAadhar}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white border-2 border-gray-200 text-gray-700 hover:text-[#00c2ab] hover:border-[#00c2ab] px-4 py-1.5 rounded-lg text-sm font-semibold transition-all shadow-sm"
                      >
                        View
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-5 mt-10">
              <button
                onClick={() => navigate('/post-job')}
                className="flex-1 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white text-lg font-bold py-4 rounded-xl shadow-lg hover:shadow-[0_8px_25px_rgba(0,194,171,0.4)] transform hover:-translate-y-1 transition-all"
              >
                Post a Job
              </button>
              <button
                onClick={() => navigate('/plans')}
                className="flex-1 bg-white border-2 border-[#00c2ab] text-[#00c2ab] text-lg font-bold py-4 rounded-xl shadow-md hover:bg-teal-50 transform hover:-translate-y-1 transition-all flex items-center justify-center gap-2"
              >
                View Plans
              </button>
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <form onSubmit={handleSave} className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 md:p-10 space-y-8">
            {/* Logo Upload */}
            <div className="pb-6 border-b border-gray-100">
              <label className="block text-sm font-bold text-gray-700 mb-4 uppercase tracking-wide">Company Logo</label>
              <div className="flex flex-col sm:flex-row gap-6 sm:items-center">
                {/* Show preview of newly selected file OR existing logo from database */}
                <div className="flex-shrink-0">
                  {formData.logo instanceof File ? (
                    <img
                      src={URL.createObjectURL(formData.logo)}
                      alt="New logo preview"
                      className="w-24 h-24 object-cover rounded-2xl shadow-md border-4 border-[#00c2ab]"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=00c2ab&color=fff&size=160&bold=true`;
                      }}
                    />
                  ) : company.logo ? (
                    <img
                      src={getFileUrl(company.logo.startsWith('uploads/') ? company.logo : `uploads/${company.logo}`)}
                      alt="Current logo"
                      className="w-24 h-24 object-cover rounded-2xl shadow-sm border border-gray-200"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(company.name)}&background=00c2ab&color=fff&size=160&bold=true`;
                      }}
                    />
                  ) : (
                    <div className="w-24 h-24 bg-teal-50 border border-teal-100 rounded-2xl flex items-center justify-center text-[#00c2ab]">
                      <FaIndustry size={32} />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    name="logo"
                    onChange={handleChange}
                    accept="image/*"
                    className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2.5 file:px-6
                          file:rounded-xl file:border-0
                          file:text-sm file:font-semibold
                          file:bg-[#00c2ab] file:text-white
                          hover:file:bg-[#00a894]
                          file:cursor-pointer file:transition-colors
                          border border-gray-300 rounded-xl shadow-sm
                          focus:outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-2 font-medium">
                    {formData.logo instanceof File ? '✓ New logo selected (will be uploaded on save)' : 'Upload a square image (JPG, PNG). Max 2MB.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Company Name <span className="text-[#00c2ab]">*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                placeholder="Enter company name"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Company Description <span className="text-[#00c2ab]">*</span></label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={5}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm resize-none"
                placeholder="Tell us about your company..."
              />
            </div>

            {/* Industry & Size */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Industry <span className="text-[#00c2ab]">*</span></label>
                <select
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                >
                  <option value="">Select industry</option>
                  {industries.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Company Size</label>
                <select
                  name="companySize"
                  value={formData.companySize}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                >
                  <option value="">Select size</option>
                  {companySizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location */}
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">City <span className="text-[#00c2ab]">*</span></label>
                <input
                  type="text"
                  name="location.city"
                  value={formData.location.city}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">State</label>
                <input
                  type="text"
                  name="location.state"
                  value={formData.location.state}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                  placeholder="State"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Country</label>
                <input
                  type="text"
                  name="location.country"
                  value={formData.location.country}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                  placeholder="Country"
                />
              </div>
            </div>

            {/* Website & Founded */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Website</label>
                <input
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Founded Year</label>
                <input
                  type="number"
                  name="founded"
                  value={formData.founded}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                  placeholder="e.g., 2020"
                />
              </div>
            </div>

            {/* Specialties & Benefits */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Specialties <span className="font-normal text-gray-400">(comma-separated)</span></label>
                <input
                  type="text"
                  name="specialties"
                  value={formData.specialties}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                  placeholder="e.g., Web Development, AI, Cloud"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Benefits <span className="font-normal text-gray-400">(comma-separated)</span></label>
                <input
                  type="text"
                  name="benefits"
                  value={formData.benefits}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                  placeholder="e.g., Health Insurance, Remote Work"
                />
              </div>
            </div>

            {/* Company Registration Documents */}
            <div className="p-8 bg-teal-50 border border-teal-100 rounded-2xl shadow-inner mt-8">
              <h4 className="font-bold mb-6 text-gray-900 text-lg flex items-center gap-2">
                <FaIndustry className="text-[#00c2ab]" /> Registration Documents
              </h4>
              <div className="space-y-6">
                {/* Aadhar Card */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Aadhar Card <span className="text-[#00c2ab]">*</span>
                  </label>
                  {documents.aadharCard && (
                    <div className="flex items-center gap-3 mb-3 bg-white p-3 rounded-xl border border-teal-100 shadow-sm">
                      <FaCheckCircle className="text-[#00c2ab] text-xl" />
                      <span className="text-sm font-medium text-gray-700 flex-1 truncate">
                        {documents.aadharCard.split('/').pop()}
                      </span>
                      <a
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/companies/${company._id}/documents/aadharCard`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-bold border-2 border-gray-200 hover:border-[#00c2ab] text-gray-600 hover:text-[#00c2ab] px-3 py-1.5 rounded-lg transition-colors"
                      >
                        View
                      </a>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleDocumentChange(e, 'aadharCard')}
                    disabled={uploadingDoc === 'aadharCard'}
                    className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-lg file:border-0
                          file:text-sm file:font-semibold
                          file:bg-white file:text-gray-700
                          file:border file:border-gray-300
                          hover:file:bg-gray-50
                          file:cursor-pointer file:transition-colors
                          border border-gray-300 rounded-xl shadow-sm bg-white
                          focus:outline-none"
                  />
                  {uploadingDoc === 'aadharCard' && (
                    <p className="text-sm text-[#00c2ab] mt-2 font-medium animate-pulse">Uploading...</p>
                  )}
                </div>

                {/* PAN Card */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    PAN Card <span className="text-[#00c2ab]">*</span>
                  </label>
                  {documents.panCard && (
                    <div className="flex items-center gap-3 mb-3 bg-white p-3 rounded-xl border border-teal-100 shadow-sm">
                      <FaCheckCircle className="text-[#00c2ab] text-xl" />
                      <span className="text-sm font-medium text-gray-700 flex-1 truncate">
                        {documents.panCard.split('/').pop()}
                      </span>
                      <a
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/companies/${company._id}/documents/panCard`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-bold border-2 border-gray-200 hover:border-[#00c2ab] text-gray-600 hover:text-[#00c2ab] px-3 py-1.5 rounded-lg transition-colors"
                      >
                        View
                      </a>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleDocumentChange(e, 'panCard')}
                    disabled={uploadingDoc === 'panCard'}
                    className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-lg file:border-0
                          file:text-sm file:font-semibold
                          file:bg-white file:text-gray-700
                          file:border file:border-gray-300
                          hover:file:bg-gray-50
                          file:cursor-pointer file:transition-colors
                          border border-gray-300 rounded-xl shadow-sm bg-white
                          focus:outline-none"
                  />
                  {uploadingDoc === 'panCard' && (
                    <p className="text-sm text-[#00c2ab] mt-2 font-medium animate-pulse">Uploading...</p>
                  )}
                </div>

                {/* GST Certificate */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    GST Certificate <span className="text-[#00c2ab]">*</span>
                  </label>
                  {documents.gstCertificate && (
                    <div className="flex items-center gap-3 mb-3 bg-white p-3 rounded-xl border border-teal-100 shadow-sm">
                      <FaCheckCircle className="text-[#00c2ab] text-xl" />
                      <span className="text-sm font-medium text-gray-700 flex-1 truncate">
                        {documents.gstCertificate.split('/').pop()}
                      </span>
                      <a
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/companies/${company._id}/documents/gstCertificate`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-bold border-2 border-gray-200 hover:border-[#00c2ab] text-gray-600 hover:text-[#00c2ab] px-3 py-1.5 rounded-lg transition-colors"
                      >
                        View
                      </a>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleDocumentChange(e, 'gstCertificate')}
                    disabled={uploadingDoc === 'gstCertificate'}
                    className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-lg file:border-0
                          file:text-sm file:font-semibold
                          file:bg-white file:text-gray-700
                          file:border file:border-gray-300
                          hover:file:bg-gray-50
                          file:cursor-pointer file:transition-colors
                          border border-gray-300 rounded-xl shadow-sm bg-white
                          focus:outline-none"
                  />
                  {uploadingDoc === 'gstCertificate' && (
                    <p className="text-sm text-[#00c2ab] mt-2 font-medium animate-pulse">Uploading...</p>
                  )}
                </div>

                {/* Udyam Aadhar */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Udyam Aadhar <span className="text-[#00c2ab]">*</span>
                  </label>
                  {documents.udyamAadhar && (
                    <div className="flex items-center gap-3 mb-3 bg-white p-3 rounded-xl border border-teal-100 shadow-sm">
                      <FaCheckCircle className="text-[#00c2ab] text-xl" />
                      <span className="text-sm font-medium text-gray-700 flex-1 truncate">
                        {documents.udyamAadhar.split('/').pop()}
                      </span>
                      <a
                        href={`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/companies/${company._id}/documents/udyamAadhar`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-bold border-2 border-gray-200 hover:border-[#00c2ab] text-gray-600 hover:text-[#00c2ab] px-3 py-1.5 rounded-lg transition-colors"
                      >
                        View
                      </a>
                    </div>
                  )}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleDocumentChange(e, 'udyamAadhar')}
                    disabled={uploadingDoc === 'udyamAadhar'}
                    className="block w-full text-sm text-gray-500
                          file:mr-4 file:py-2 file:px-4
                          file:rounded-lg file:border-0
                          file:text-sm file:font-semibold
                          file:bg-white file:text-gray-700
                          file:border file:border-gray-300
                          hover:file:bg-gray-50
                          file:cursor-pointer file:transition-colors
                          border border-gray-300 rounded-xl shadow-sm bg-white
                          focus:outline-none"
                  />
                  {uploadingDoc === 'udyamAadhar' && (
                    <p className="text-sm text-[#00c2ab] mt-2 font-medium animate-pulse">Uploading...</p>
                  )}
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="p-8 bg-white border border-gray-100 shadow-sm rounded-2xl">
              <h4 className="font-bold mb-6 text-gray-900 text-lg">Social Media Links</h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">LinkedIn</label>
                  <input
                    type="url"
                    name="socialLinks.linkedin"
                    value={formData.socialLinks.linkedin}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                    placeholder="LinkedIn URL"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Twitter</label>
                  <input
                    type="url"
                    name="socialLinks.twitter"
                    value={formData.socialLinks.twitter}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                    placeholder="Twitter URL"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Facebook</label>
                  <input
                    type="url"
                    name="socialLinks.facebook"
                    value={formData.socialLinks.facebook}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                    placeholder="Facebook URL"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Instagram</label>
                  <input
                    type="url"
                    name="socialLinks.instagram"
                    value={formData.socialLinks.instagram}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#00c2ab] focus:border-transparent transition-all shadow-sm"
                    placeholder="Instagram URL"
                  />
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4 pt-8 mt-8 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="flex-1 px-6 py-4 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 hover:border-gray-300 font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <FaTimes /> Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-[#00c2ab] to-[#009688] text-white rounded-xl hover:shadow-lg hover:shadow-teal-500/30 font-bold text-lg transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-2"
              >
                <FaSave /> {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CompanyProfile;
